import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import {
  ArrowLeft, Users, ClipboardText, ChartBar, Plus, Trash, Copy, Calendar,
  CheckCircle, Circle, X, Sparkle, Play, UploadSimple, UserMinus, DownloadSimple, Printer, Megaphone,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = [
  { key: "roster", label: "Roster", icon: <Users size={14}/> },
  { key: "assignments", label: "Assignments", icon: <ClipboardText size={14}/> },
  { key: "progress", label: "Progress", icon: <ChartBar size={14}/> },
];

export default function ClassDetail() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("roster");
  const [myDecks, setMyDecks] = useState([]);

  // Invite form
  const [inviteText, setInviteText] = useState("");
  const [inviting, setInviting] = useState(false);

  // New assignment form
  const [showAssign, setShowAssign] = useState(false);
  const [assignDeckId, setAssignDeckId] = useState("");
  const [assignTitle, setAssignTitle] = useState("");
  const [assignDueAt, setAssignDueAt] = useState(""); // local date string yyyy-mm-dd
  const [assigning, setAssigning] = useState(false);
  const inviteRef = useRef(null);
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState("");
  const [postingAnnouncement, setPostingAnnouncement] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/classes/${classId}`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load class");
      navigate("/classes");
    } finally { setLoading(false); }
  };

  const loadProgress = async () => {
    if (!detail?.is_teacher) return;
    try {
      const { data } = await api.get(`/classes/${classId}/progress`);
      setProgress(data);
    } catch {
      toast.error("Could not load progress");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classId]);
  useEffect(() => {
    // Load announcements whenever class detail is loaded
    if (!detail) return;
    (async () => {
      try {
        const { data } = await api.get(`/classes/${classId}/announcements`);
        setAnnouncements(data.announcements || []);
      } catch { /* silent */ }
    })();
  }, [detail, classId]);
  useEffect(() => {
    if (tab === "progress") loadProgress();
    if (tab === "assignments" && detail?.is_teacher && myDecks.length === 0) {
      api.get("/decks").then(({ data }) => setMyDecks(data.decks || [])).catch(() => {});
    }
    // eslint-disable-next-line
  }, [tab, detail?.is_teacher]);

  const cls = detail?.class;
  const isTeacher = !!detail?.is_teacher;
  const members = detail?.members || [];
  const assignments = detail?.assignments || [];

  const copyCode = () => {
    if (!cls?.join_code) return;
    navigator.clipboard?.writeText(cls.join_code);
    toast.success(`Join code ${cls.join_code} copied`);
  };

  const copyInviteLink = () => {
    if (!cls?.join_code) return;
    const url = `${window.location.origin}/classes?code=${cls.join_code}`;
    navigator.clipboard?.writeText(url);
    toast.success("Invite link copied");
  };

  const doInvite = async () => {
    const emails = inviteText.split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s && s.includes("@"));
    if (!emails.length) { toast.error("Paste at least one email"); return; }
    setInviting(true);
    try {
      const { data } = await api.post(`/classes/${classId}/invite`, { emails });
      toast.success(`Added ${data.added}, ${data.already} already in class`);
      setInviteText("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not invite");
    } finally { setInviting(false); }
  };

  const removeMember = async (userId, email) => {
    toast(`Remove ${email} from this class?`, {
      duration: 8000,
      action: {
        label: "Remove",
        onClick: async () => {
          try {
            await api.delete(`/classes/${classId}/members/${userId}`);
            toast.success("Removed");
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Remove failed");
          }
        },
      },
    });
  };

  const onCsvFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const text = await f.text();
    // Grab every email-looking token from the CSV
    const matches = text.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g) || [];
    if (!matches.length) { toast.error("No emails found in file"); return; }
    setInviteText((cur) => (cur ? cur + "\n" : "") + matches.join("\n"));
    toast.success(`Found ${matches.length} email${matches.length === 1 ? "" : "s"}`);
    inviteRef.current?.focus();
  };

  const createAssignment = async () => {
    if (!assignDeckId) { toast.error("Pick a deck"); return; }
    setAssigning(true);
    try {
      const body = {
        deck_id: assignDeckId,
        title: assignTitle.trim() || null,
      };
      if (assignDueAt) {
        // Convert yyyy-mm-dd to ISO (end of day local)
        const d = new Date(assignDueAt + "T23:59:59");
        body.due_at = d.toISOString();
      }
      await api.post(`/classes/${classId}/assignments`, body);
      toast.success("Assignment created");
      setShowAssign(false);
      setAssignDeckId(""); setAssignTitle(""); setAssignDueAt("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally { setAssigning(false); }
  };

  const deleteAssignment = async (assignmentId, title) => {
    toast(`Remove assignment "${title}"?`, {
      duration: 8000,
      action: {
        label: "Delete",
        onClick: async () => {
          try {
            await api.delete(`/classes/${classId}/assignments/${assignmentId}`);
            toast.success("Removed");
            load();
          } catch {
            toast.error("Delete failed");
          }
        },
      },
    });
  };

  const deleteClass = async () => {
    toast(`Delete the entire class "${cls.name}"? This can't be undone.`, {
      duration: 10000,
      action: {
        label: "Delete class",
        onClick: async () => {
          try {
            await api.delete(`/classes/${classId}`);
            toast.success("Class deleted");
            navigate("/classes");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
          }
        },
      },
    });
  };

  const postAnnouncement = async () => {
    const body = newAnnouncement.trim();
    if (!body) return;
    setPostingAnnouncement(true);
    try {
      const { data } = await api.post(`/classes/${classId}/announcements`, { body });
      setAnnouncements((a) => [data, ...a]);
      setNewAnnouncement("");
      toast.success("Announcement posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post");
    } finally { setPostingAnnouncement(false); }
  };

  const deleteAnnouncement = async (announcementId) => {
    try {
      await api.delete(`/classes/${classId}/announcements/${announcementId}`);
      setAnnouncements((a) => a.filter((x) => x.announcement_id !== announcementId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const exportProgressCsv = () => {
    if (!progress || !progress.students?.length) {
      toast.error("No progress data to export yet");
      return;
    }
    const assignments = progress.assignments || [];
    const header = [
      "Student name", "Email", "Status",
      ...assignments.flatMap((a) => [
        `${a.title} — Mastery (%)`,
        `${a.title} — Accuracy (%)`,
        `${a.title} — Cards mastered`,
        `${a.title} — Total cards`,
        `${a.title} — Completed (Y/N)`,
        `${a.title} — Last attempt`,
      ]),
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = progress.students.map((s) => {
      const cells = [s.name || "", s.email || "", s.status || ""];
      for (const a of assignments) {
        const p = (s.per_assignment || []).find((x) => x.assignment_id === a.assignment_id);
        if (!p || !p.total_cards) {
          cells.push("", "", "", "", "", "");
        } else {
          const masteryPct = Math.round((p.cards_mastered / (p.total_cards || 1)) * 100);
          cells.push(
            masteryPct,
            p.accuracy ?? 0,
            p.cards_mastered ?? 0,
            p.total_cards ?? 0,
            p.completed ? "Y" : "N",
            p.last_attempt_at || "",
          );
        }
      }
      return cells.map(escape).join(",");
    });
    const csv = [header.map(escape).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    const safeName = (cls?.name || "class").replace(/[^\w-]+/g, "_");
    a.href = url;
    a.download = `ekkol-${safeName}-progress-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Progress CSV downloaded");
  };

  const printRoster = () => {
    if (!members.length) {
      toast.error("No members yet to print");
      return;
    }
    const rows = members
      .map(
        (m, i) => `
        <tr>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;">${i + 1}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;">${(m.name || "").replace(/</g, "&lt;")}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;color:#666;">${(m.email || "").replace(/</g, "&lt;")}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;text-transform:uppercase;font-size:10px;letter-spacing:2px;">${m.role}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;text-transform:uppercase;font-size:10px;letter-spacing:2px;color:${m.status === "invited" ? "#d9381e" : "#999"};">${m.status}</td>
        </tr>`,
      )
      .join("");
    const html = `<!doctype html><html><head><title>Roster — ${cls.name}</title>
      <style>
        body{font-family:Georgia,serif;padding:40px;color:#0a192f;}
        h1{font-size:32px;margin:0 0 4px;}
        .meta{color:#666;font-family:'IBM Plex Mono',monospace;font-size:11px;text-transform:uppercase;letter-spacing:2px;margin-bottom:24px;}
        .code{display:inline-block;background:#0a192f;color:#fff;padding:6px 12px;font-family:monospace;letter-spacing:4px;margin-left:8px;}
        table{width:100%;border-collapse:collapse;margin-top:16px;font-size:14px;}
        th{text-align:left;padding:8px 12px;font-family:'IBM Plex Mono',monospace;font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#666;border-bottom:2px solid #0a192f;}
        @media print{body{padding:16px;}}
      </style></head><body>
      <h1>${cls.name}</h1>
      <div class="meta">Ekkol · Class roster · Join code <span class="code">${cls.join_code}</span> · ${members.length} members · printed ${new Date().toLocaleDateString()}</div>
      <table>
        <thead><tr><th>#</th><th>Name</th><th>Email</th><th>Role</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <script>window.onload=function(){window.print();};</script>
    </body></html>`;
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Pop-up blocked — allow pop-ups and retry"); return; }
    w.document.write(html);
    w.document.close();
  };

  if (loading || !detail) return (
    <div className="min-h-screen bg-[color:var(--paper)]"><Navbar/>
      <div className="max-w-5xl mx-auto py-20 px-6"><div className="wave"><span/><span/><span/><span/><span/></div></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 fade-in">
        <Link to="/classes" className="text-sm link-underline inline-flex items-center gap-1 mb-8" data-testid="back-to-classes">
          <ArrowLeft size={14} /> All classes
        </Link>

        <div className="flex items-start justify-between flex-wrap gap-6 border-b rule pb-8 mb-8">
          <div className="flex-1 min-w-[260px]">
            <div className="eyebrow mb-3">{isTeacher ? "Teaching" : "Enrolled"}</div>
            <h1 className="font-serif text-4xl sm:text-5xl leading-none tracking-tight" data-testid="class-name">{cls.name}</h1>
            {cls.description && <p className="text-[color:var(--ink-muted)] mt-3 max-w-2xl">{cls.description}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em]">
                <span className="text-[color:var(--ink-muted)]">Join code</span>
                <span className="bg-[color:var(--ink)] text-white px-3 py-1.5 text-base tracking-[0.3em]" data-testid="class-join-code">
                  {cls.join_code}
                </span>
                <button onClick={copyCode} className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)] p-1" title="Copy code" data-testid="btn-copy-code">
                  <Copy size={14}/>
                </button>
                {isTeacher && (
                  <button onClick={copyInviteLink} className="text-xs link-underline ml-2" data-testid="btn-copy-invite-link">
                    copy invite link
                  </button>
                )}
              </div>
            </div>
          </div>
          {isTeacher && (
            <button onClick={deleteClass} className="text-xs text-[color:var(--vermilion)] hover:underline font-mono uppercase tracking-widest" data-testid="btn-delete-class">
              Delete class
            </button>
          )}
        </div>

        {/* Announcements */}
        <section className="mb-8" data-testid="announcements-section">
          <div className="flex items-center gap-2 eyebrow mb-3">
            <Megaphone size={14}/> Announcements
          </div>
          {isTeacher && (
            <div className="border rule bg-white p-4 mb-3 flex items-start gap-3">
              <textarea
                value={newAnnouncement}
                onChange={(e) => setNewAnnouncement(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postAnnouncement(); }}
                rows={2}
                placeholder="Post a quick note for your class. Cmd/Ctrl + Enter to send."
                className="flex-1 bg-[color:var(--paper)] border rule px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--ink)] font-serif"
                data-testid="input-announcement"
                maxLength={2000}
              />
              <button
                onClick={postAnnouncement}
                disabled={postingAnnouncement || !newAnnouncement.trim()}
                className="btn-primary py-2 px-4 text-sm whitespace-nowrap"
                data-testid="btn-post-announcement"
              >
                <Megaphone size={14} weight="fill"/> Post
              </button>
            </div>
          )}
          {announcements.length === 0 ? (
            !isTeacher && (
              <div className="border rule bg-white p-5 text-sm text-[color:var(--ink-muted)] text-center">
                No announcements from your teacher yet.
              </div>
            )
          ) : (
            <div className="space-y-2" data-testid="announcements-list">
              {announcements.slice(0, 5).map((a) => (
                <div
                  key={a.announcement_id}
                  className="border rule bg-white p-4 flex items-start justify-between gap-4"
                  data-testid={`announcement-${a.announcement_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-serif leading-relaxed whitespace-pre-wrap">{a.body}</p>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-faint)] mt-2">
                      {a.author_name || "Teacher"} · {new Date(a.created_at).toLocaleString()}
                    </div>
                  </div>
                  {isTeacher && (
                    <button
                      onClick={() => deleteAnnouncement(a.announcement_id)}
                      className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)] p-1"
                      title="Delete"
                      data-testid={`btn-del-announcement-${a.announcement_id}`}
                    >
                      <Trash size={14}/>
                    </button>
                  )}
                </div>
              ))}
              {announcements.length > 5 && (
                <p className="text-xs text-[color:var(--ink-faint)] font-mono text-center">
                  Showing latest 5 of {announcements.length} announcements
                </p>
              )}
            </div>
          )}
        </section>

        {/* Tabs */}
        <div className="flex items-center gap-px bg-[color:var(--rule)] border rule mb-8" data-testid="class-tabs">
          {TABS.filter((t) => isTeacher || t.key !== "progress").map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-3 px-4 text-sm font-mono uppercase tracking-widest flex items-center justify-center gap-2 transition-colors ${
                tab === t.key ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-70 hover:opacity-100"
              }`}
              data-testid={`tab-${t.key}`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ROSTER TAB */}
        {tab === "roster" && (
          <div className="space-y-6" data-testid="tab-content-roster">
            {isTeacher && (
              <div className="border rule bg-white p-6">
                <div className="eyebrow mb-3">Invite students</div>
                <p className="text-sm text-[color:var(--ink-muted)] mb-4">
                  Paste email addresses (comma, space, or line-separated), or upload a CSV roster. Students already in
                  Ekkol join instantly; unregistered students are held as invited until they sign in.
                </p>
                <textarea
                  ref={inviteRef}
                  value={inviteText}
                  onChange={(e) => setInviteText(e.target.value)}
                  rows={3}
                  placeholder="alex@school.edu, sam@school.edu&#10;jordan@school.edu"
                  className="w-full bg-[color:var(--paper)] border rule px-4 py-3 font-mono text-sm focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="input-invite-emails"
                />
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <button onClick={doInvite} disabled={inviting || !inviteText.trim()} className="btn-primary" data-testid="btn-send-invites">
                    <Plus size={14} weight="bold"/> {inviting ? "Adding…" : "Add to class"}
                  </button>
                  <label className="text-xs flex items-center gap-1 link-underline cursor-pointer">
                    <UploadSimple size={14}/> Upload CSV
                    <input type="file" accept=".csv,.txt" onChange={onCsvFile} className="hidden"/>
                  </label>
                  <span className="text-xs text-[color:var(--ink-faint)] font-mono">
                    Or share the code: <strong>{cls.join_code}</strong>
                  </span>
                </div>
              </div>
            )}

            <div className="border rule bg-white divide-y rule" data-testid="members-list">
              <div className="px-5 py-3 flex items-center justify-between bg-[color:var(--surface-alt)]">
                <div className="eyebrow">Members · {members.length}</div>
                {isTeacher && members.length > 0 && (
                  <button
                    onClick={printRoster}
                    className="text-xs flex items-center gap-1 link-underline text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                    data-testid="btn-print-roster-tab"
                    title="Printable roster with join code"
                  >
                    <Printer size={12}/> Print roster
                  </button>
                )}
              </div>
              {members.length === 0 ? (
                <div className="p-10 text-center text-sm text-[color:var(--ink-muted)]">
                  No members yet. {isTeacher && "Invite students above."}
                </div>
              ) : members.map((m) => (
                <div key={m.user_id + m.email} className="p-5 flex items-center justify-between gap-4" data-testid={`member-${m.email}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg truncate">{m.name || m.email}</div>
                    <div className="text-xs text-[color:var(--ink-muted)] truncate">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border rule ${m.role === "teacher" ? "bg-[color:var(--ink)] text-white border-[color:var(--ink)]" : ""}`}>
                      {m.role}
                    </span>
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${m.status === "invited" ? "text-[color:var(--vermilion)]" : "text-[color:var(--ink-muted)]"}`}>
                      {m.status}
                    </span>
                    {isTeacher && m.role !== "teacher" && (
                      <button
                        onClick={() => removeMember(m.user_id, m.email)}
                        className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)] p-1"
                        title="Remove from class"
                        data-testid={`btn-remove-${m.email}`}
                      >
                        <UserMinus size={16}/>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ASSIGNMENTS TAB */}
        {tab === "assignments" && (
          <div className="space-y-6" data-testid="tab-content-assignments">
            {isTeacher && (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-[color:var(--ink-muted)]">
                  Assign one of your own decks as homework. Students see it instantly in their library.
                </p>
                <button onClick={() => setShowAssign(true)} className="btn-primary" data-testid="btn-new-assignment">
                  <Plus size={14} weight="bold"/> New assignment
                </button>
              </div>
            )}

            {/* Student prep note */}
            {!isTeacher && assignments.length > 0 && (
              <div className="border-l-4 border-[color:var(--oxford)] bg-white px-5 py-4" data-testid="student-prep-note">
                <div className="eyebrow text-[color:var(--oxford)] mb-1">Before you practice</div>
                <p className="text-sm text-[color:var(--ink-muted)] leading-relaxed">
                  Ekkol helps you <strong>remember</strong> what you&apos;ve already learned — it doesn&apos;t teach it for
                  the first time. Review your class notes, textbook chapter, or lecture slides first. Then come back
                  and practice in short sessions (5-10 minutes, several times a week beats a one-hour cram). Stuck
                  on a card? Tap the <strong>💡 hint</strong> button for small clues.
                </p>
              </div>
            )}

            {assignments.length === 0 ? (
              <div className="border rule bg-white p-10 text-center">
                <ClipboardText size={36} weight="duotone" className="mx-auto text-[color:var(--ink-muted)]"/>
                <p className="mt-4 font-serif text-xl">No assignments yet.</p>
                {!isTeacher && (
                  <p className="mt-2 text-sm text-[color:var(--ink-muted)]">Your teacher hasn&apos;t assigned anything yet.</p>
                )}
              </div>
            ) : (
              <div className="border rule bg-white divide-y rule" data-testid="assignments-list">
                {assignments.map((a) => (
                  <div key={a.assignment_id} className="p-5 flex items-start justify-between gap-4" data-testid={`assignment-${a.assignment_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-xl leading-snug">{a.title}</div>
                      <div className="text-xs text-[color:var(--ink-muted)] mt-1 flex flex-wrap items-center gap-3">
                        <span>{a.deck_card_count} cards</span>
                        <span>·</span>
                        <span>{a.deck_mode === "qa" ? "Flashcards" : "Script"}</span>
                        {a.due_at && (
                          <>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1 text-[color:var(--vermilion)]">
                              <Calendar size={12}/> due {new Date(a.due_at).toLocaleDateString()}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link to={`/study/${a.deck_id}`} className="btn-primary py-2 px-4 text-sm" data-testid={`btn-study-assignment-${a.assignment_id}`}>
                        <Play size={14} weight="fill"/> {isTeacher ? "Preview" : "Study"}
                      </Link>
                      {isTeacher && (
                        <button
                          onClick={() => deleteAssignment(a.assignment_id, a.title)}
                          className="text-[color:var(--ink-faint)] hover:text-[color:var(--vermilion)] p-2"
                          title="Remove assignment"
                          data-testid={`btn-del-assignment-${a.assignment_id}`}
                        >
                          <Trash size={16}/>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PROGRESS TAB (teacher only) */}
        {tab === "progress" && isTeacher && (
          <div data-testid="tab-content-progress">
            {progress && progress.students?.length > 0 && progress.assignments?.length > 0 && (
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <p className="text-xs text-[color:var(--ink-muted)] font-mono uppercase tracking-widest">
                  Green check = ≥80% mastery
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportProgressCsv}
                    className="btn-ghost py-2 px-3 text-xs"
                    data-testid="btn-export-progress-csv"
                    title="Download as CSV for your gradebook"
                  >
                    <DownloadSimple size={14}/> Export CSV
                  </button>
                  <button
                    onClick={printRoster}
                    className="btn-ghost py-2 px-3 text-xs"
                    data-testid="btn-print-roster"
                    title="Printable roster with join code"
                  >
                    <Printer size={14}/> Print roster
                  </button>
                </div>
              </div>
            )}
            {!progress ? (
              <div className="wave"><span/><span/><span/><span/><span/></div>
            ) : progress.students.length === 0 || progress.assignments.length === 0 ? (
              <div className="border rule bg-white p-10 text-center">
                <ChartBar size={36} weight="duotone" className="mx-auto text-[color:var(--ink-muted)]"/>
                <p className="mt-4 font-serif text-xl">No progress data yet.</p>
                <p className="mt-2 text-sm text-[color:var(--ink-muted)]">
                  Progress appears once students start practicing an assignment.
                </p>
              </div>
            ) : (
              <div className="border rule bg-white overflow-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-[color:var(--surface-alt)]">
                    <tr>
                      <th className="text-left px-5 py-3 eyebrow sticky left-0 bg-[color:var(--surface-alt)]">Student</th>
                      {progress.assignments.map((a) => (
                        <th key={a.assignment_id} className="text-left px-5 py-3 eyebrow whitespace-nowrap">{a.title}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {progress.students.map((s) => (
                      <tr key={s.user_id + s.email} className="border-t rule hover:bg-[color:var(--surface-alt)]/50" data-testid={`progress-row-${s.email}`}>
                        <td className="px-5 py-3 sticky left-0 bg-white">
                          <div className="font-serif text-base truncate max-w-[220px]">{s.name || s.email}</div>
                          <div className="text-xs text-[color:var(--ink-muted)] truncate max-w-[220px]">{s.email}</div>
                          {s.status === "invited" && (
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--vermilion)]">not signed in yet</div>
                          )}
                        </td>
                        {progress.assignments.map((a) => {
                          const cell = (s.per_assignment || []).find((p) => p.assignment_id === a.assignment_id);
                          if (!cell || cell.total_cards === 0) {
                            return <td key={a.assignment_id} className="px-5 py-3 text-xs text-[color:var(--ink-faint)] font-mono">—</td>;
                          }
                          const pct = Math.round((cell.cards_mastered / (cell.total_cards || 1)) * 100);
                          return (
                            <td key={a.assignment_id} className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                {cell.completed ? (
                                  <CheckCircle size={16} weight="fill" className="text-[color:var(--success)]"/>
                                ) : cell.cards_attempted > 0 ? (
                                  <Circle size={16} className="text-[color:var(--vermilion)]"/>
                                ) : (
                                  <Circle size={16} className="text-[color:var(--ink-faint)]"/>
                                )}
                                <div>
                                  <div className="font-mono text-xs">{cell.cards_mastered} / {cell.total_cards} mastered</div>
                                  <div className="text-[10px] text-[color:var(--ink-muted)] font-mono">
                                    {pct}% · {cell.accuracy}% accuracy
                                  </div>
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* New Assignment modal */}
        {showAssign && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 fade-in"
            onClick={(e) => { if (e.target === e.currentTarget) setShowAssign(false); }}
            data-testid="new-assignment-modal"
          >
            <div className="bg-white border rule w-full max-w-md">
              <div className="flex items-center justify-between border-b rule px-6 py-4">
                <div>
                  <div className="eyebrow">Assign a deck</div>
                  <h2 className="font-serif text-2xl leading-none mt-1">New assignment</h2>
                </div>
                <button onClick={() => setShowAssign(false)} aria-label="Close" className="text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"><X size={20}/></button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className="eyebrow block mb-2">Deck</label>
                  <select
                    value={assignDeckId}
                    onChange={(e) => {
                      setAssignDeckId(e.target.value);
                      const d = myDecks.find((x) => x.deck_id === e.target.value);
                      if (d && !assignTitle) setAssignTitle(d.title || "");
                    }}
                    className="w-full bg-white border rule px-4 py-3 text-base focus:outline-none focus:border-[color:var(--ink)]"
                    data-testid="select-assign-deck"
                  >
                    <option value="">Choose one of your decks…</option>
                    {myDecks.map((d) => (
                      <option key={d.deck_id} value={d.deck_id}>{d.title} · {d.card_count ?? 0} cards</option>
                    ))}
                  </select>
                  {myDecks.length === 0 && (
                    <p className="text-xs text-[color:var(--ink-muted)] mt-2">
                      You don&apos;t have any decks yet. <Link to="/decks/new" className="link-underline">Make one first</Link>.
                    </p>
                  )}
                </div>
                <div>
                  <label className="eyebrow block mb-2">Title (optional)</label>
                  <input
                    value={assignTitle}
                    onChange={(e) => setAssignTitle(e.target.value)}
                    placeholder="Defaults to the deck's title"
                    className="w-full bg-white border rule px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                    data-testid="input-assign-title"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Due date (optional)</label>
                  <input
                    type="date"
                    value={assignDueAt}
                    onChange={(e) => setAssignDueAt(e.target.value)}
                    className="w-full bg-white border rule px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--ink)]"
                    data-testid="input-assign-due"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t rule px-6 py-4">
                <button onClick={() => setShowAssign(false)} className="btn-ghost">Cancel</button>
                <button onClick={createAssignment} disabled={assigning || !assignDeckId} className="btn-primary" data-testid="btn-assign-confirm">
                  <Sparkle size={14} weight="fill"/> {assigning ? "Assigning…" : "Assign"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
